// Build the list of deliverable files from a request object so the folder
// downloader knows exactly which URLs to grab and what filenames to use.
//
// Returned shape:
//   [{ url: "/api/uploads/abc.mp4", filename: "video.mp4" }, ...]

function basename(url) {
    if (!url) return "";
    return url.split("/").pop().split("?")[0];
}

function extOf(url, fallback = "") {
    const b = basename(url);
    const i = b.lastIndexOf(".");
    return i >= 0 ? b.slice(i) : fallback;
}

/**
 * @param {object} req - request document
 * @param {string} [folderPrefix] - human-readable address used to name files
 * @returns {Array<{url:string, filename:string}>}
 */
export function buildProjectAssetList(req, folderPrefix = "project") {
    if (!req) return [];
    const safe = (folderPrefix || "project").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_").trim();
    const out = [];

    if (req.final_video_url) {
        out.push({ url: req.final_video_url, filename: `${safe} - video${extOf(req.final_video_url, ".mp4")}` });
    }
    // The reel can be stored under either `reel_url` (legacy) or `final_reel_url`
    const reel = req.final_reel_url || req.reel_url;
    if (reel) {
        out.push({ url: reel, filename: `${safe} - reel${extOf(reel, ".mp4")}` });
    }
    if (Array.isArray(req.ai_photo_urls)) {
        req.ai_photo_urls.forEach((u, i) => {
            out.push({ url: u, filename: `${safe} - AI foto ${String(i + 1).padStart(2, "0")}${extOf(u, ".jpg")}` });
        });
    }
    if (Array.isArray(req.photo_urls)) {
        req.photo_urls.forEach((u, i) => {
            out.push({ url: u, filename: `${safe} - foto ${String(i + 1).padStart(2, "0")}${extOf(u, ".jpg")}` });
        });
    }
    if (Array.isArray(req.document_urls)) {
        req.document_urls.forEach((u, i) => {
            const orig = basename(u);
            out.push({ url: u, filename: `${safe} - doc ${String(i + 1).padStart(2, "0")} - ${orig}` });
        });
    }
    return out;
}

/** Just the agent photos (the "alles downloaden" button under the photo gallery).
 *  Includes BOTH the displayed photos (`ai_photo_urls` if present, otherwise
 *  `photo_urls`) AND any underlying originals — either the bulk `photo_urls`
 *  array OR per-index `ai_photo_previous_urls` for AI-replaced tiles.
 *  Files are prefixed with "AI - " / "Origineel - " when both sets exist. */
export function buildAgentPhotoList(req, folderPrefix = "fotos") {
    if (!req) return [];
    const ai = Array.isArray(req.ai_photo_urls) ? req.ai_photo_urls : [];
    const photos = Array.isArray(req.photo_urls) ? req.photo_urls : [];
    const prev = (req.ai_photo_previous_urls && typeof req.ai_photo_previous_urls === "object")
        ? req.ai_photo_previous_urls
        : {};
    const safe = (folderPrefix || "fotos").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_").trim();

    // The "displayed" set we ship as-is (AI-edited gallery)
    const displayed = ai.length > 0 ? ai : photos;
    // The "originals" we ship alongside — prefer the bulk photo_urls array;
    // fall back to per-index previous URLs when photo_urls is empty.
    let originals = [];
    if (ai.length > 0 && photos.length > 0) {
        originals = photos.map((u, i) => ({ url: u, idx: i + 1 }));
    } else if (ai.length > 0) {
        originals = Object.entries(prev)
            .filter(([, u]) => !!u)
            .map(([k, u]) => ({ url: u, idx: parseInt(k, 10) + 1 }));
    }
    const hasBoth = displayed.length > 0 && originals.length > 0;
    const out = [];
    displayed.forEach((u, i) => {
        const prefix = hasBoth ? "AI - " : "";
        out.push({
            url: u,
            filename: `${prefix}${safe} - foto ${String(i + 1).padStart(2, "0")}${extOf(u, ".jpg")}`,
        });
    });
    originals.forEach(({ url, idx }) => {
        const prefix = hasBoth ? "Origineel - " : "";
        out.push({
            url,
            filename: `${prefix}${safe} - foto ${String(idx).padStart(2, "0")}${extOf(url, ".jpg")}`,
        });
    });
    return out;
}
